import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { Field } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';

export default function Login() {
  const { user, login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  // Defer loading the big MP4 until after first paint (or idle).
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      setVideoReady(true);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(start, { timeout: 1800 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(id);
      };
    }
    const tmr = setTimeout(start, 600);
    return () => {
      cancelled = true;
      clearTimeout(tmr);
    };
  }, []);

  useEffect(() => {
    if (!videoReady || !videoRef.current) return;
    const el = videoRef.current;
    // Explicit load + play after source is in the DOM
    el.load();
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [videoReady]);

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requiresTwoFactor) {
        setNeedsTwoFactor(true);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || t('Login failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitTwoFactor(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyTwoFactor(useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() });
      navigate('/');
    } catch (err) {
      setError(err.message || t('Invalid code'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      {/* Static poster first — video mounts only after idle so sign-in is instant */}
      <div
        className="login-bg-poster"
        style={{ backgroundImage: 'url(/icons/icon-512.png)' }}
        aria-hidden
      />
      {videoReady && !videoFailed && (
        <video
          ref={videoRef}
          className="login-bg-video"
          muted
          loop
          playsInline
          preload="none"
          poster="/icons/icon-512.png"
          onError={() => setVideoFailed(true)}
        >
          <source src="/video/login-bg.mp4" type="video/mp4" />
        </video>
      )}
      <div className="login-bg-overlay" />
      <div className="login-bg-grid" />

      {!needsTwoFactor ? (
        <form className="login-card fade-in" onSubmit={submit}>
          <div className="login-brand">
            <span className="brand-mark lg">A</span>
            <h1>Agevolami</h1>
            <p>{t('Project & contract management')}</p>
          </div>

          {error && <div className="alert error">{error}</div>}

          <Field label={t('Email')}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   autoComplete="username" placeholder={t('you@company.com')} required />
          </Field>
          <Field label={t('Password')}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   autoComplete="current-password" placeholder="••••••••" required />
          </Field>

          <button className="btn primary block" disabled={busy}>
            {busy ? t('Signing in…') : t('Sign in')}
          </button>

          <p className="muted sm" style={{ textAlign: 'center', marginTop: 14 }}>
            {t('Forgot your password? Contact your administrator to reset it.')}
          </p>
        </form>
      ) : (
        <form className="login-card fade-in" onSubmit={submitTwoFactor}>
          <div className="login-brand">
            <span className="brand-mark lg">A</span>
            <h1>Agevolami</h1>
            <p>{t('Enter the code from your authenticator app')}</p>
          </div>

          {error && <div className="alert error">{error}</div>}

          <Field label={useRecovery ? t('Recovery code') : t('Authentication code')}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              placeholder={useRecovery ? 'XXXX-XXXX-XXXX' : '123456'}
              autoFocus
              required
            />
          </Field>

          <button className="btn primary block" disabled={busy}>
            {busy ? t('Verifying…') : t('Verify')}
          </button>

          <p className="muted sm" style={{ textAlign: 'center', marginTop: 14 }}>
            <button type="button" className="btn ghost sm" onClick={() => { setUseRecovery((v) => !v); setCode(''); setError(''); }}>
              {useRecovery ? t('Use authenticator code instead') : t('Use a recovery code instead')}
            </button>
          </p>
        </form>
      )}
    </div>
  );
}
